import type { TestedAssetSharedContext } from "./draft.generated";

/**
 * The tested set's shared context: the one reader of what every tested item is
 * shown with. The compiler puts it on the asset catalog
 * (`assetContext.sharedContext`), which the engine prints to twins with every
 * stimulus and the Preview shows; the editor's assets panel shows the same
 * text read from the draft (gate 2026-10-16, CT-07: twins read it and no
 * builder surface showed it). Moved here from Nest's `concept-assets.ts` so
 * Nest and the editor read one owner.
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Preserve only a complete literal shared-context record.
 *
 * This is not a semantic repair step. The extractor/re-plan model decides
 * whether text is common respondent material; this boundary only verifies the
 * typed provenance and that the stored text is exactly the ordered source
 * fragments it claims to be.
 */
function literalSharedContext(raw: unknown): TestedAssetSharedContext | undefined {
  if (!isRecord(raw) || raw.schemaVersion !== "tested-asset-shared-context.v1") {
    return undefined;
  }
  const provenance = isRecord(raw.provenance) ? raw.provenance : undefined;
  if (
    typeof raw.text !== "string" ||
    !raw.text.trim() ||
    provenance?.kind !== "literal_source_segments" ||
    !Array.isArray(provenance.segments) ||
    provenance.segments.length === 0
  ) {
    return undefined;
  }
  const segments = provenance.segments.flatMap((segment: unknown) => {
    if (!isRecord(segment)) return [];
    if (
      typeof segment.sourceId !== "string" ||
      !segment.sourceId.trim() ||
      typeof segment.text !== "string" ||
      !segment.text.trim()
    ) {
      return [];
    }
    return [{ sourceId: segment.sourceId, text: segment.text }];
  });
  if (
    segments.length !== provenance.segments.length ||
    segments.map((segment) => segment.text).join("\n") !== raw.text
  ) {
    return undefined;
  }
  return {
    schemaVersion: "tested-asset-shared-context.v1",
    text: raw.text,
    provenance: { kind: "literal_source_segments", segments },
  };
}

/**
 * A shared-context record is a separate respondent surface only when it adds
 * literal material beyond the individual asset cards. The extractor sometimes
 * returns each asset's complete description again as ordered "shared" text.
 * That has no distinct source fact: it doubles prompt and stimulus context,
 * then makes REPLAN choose an unnecessary context operation for an ordinary
 * asset edit. This is an exact structural comparison, not language inference.
 */
function isExactOrderedAssetDescriptionDuplicate(
  sharedContext: TestedAssetSharedContext,
  assets: ReadonlyArray<{ description?: unknown }>,
): boolean {
  if (assets.length !== sharedContext.provenance.segments.length) return false;
  return assets.every(
    (asset, index) =>
      typeof asset.description === "string" &&
      asset.description === sharedContext.provenance.segments[index]?.text,
  );
}

/**
 * The set's shared-context record as respondents get it, or undefined: a
 * complete literal record that is not the items' own descriptions again.
 */
export function testedAssetSharedContext(
  raw: unknown,
  assets: ReadonlyArray<{ description?: unknown }>,
): TestedAssetSharedContext | undefined {
  const sharedContext = literalSharedContext(raw);
  return sharedContext && !isExactOrderedAssetDescriptionDuplicate(sharedContext, assets)
    ? sharedContext
    : undefined;
}

/** The exact common respondent context, or undefined when none was established. */
export function readTestedAssetSharedContext(testedAssets: unknown): string | undefined {
  if (!isRecord(testedAssets)) return undefined;
  const assets = Array.isArray(testedAssets.assets) ? testedAssets.assets.filter(isRecord) : [];
  return testedAssetSharedContext(testedAssets.sharedContext, assets)?.text;
}
